import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Barcode, 
  Plus, 
  Upload, 
  Image as ImageIcon,
  Package,
  Clock,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Loader2,
  Eye,
  Trash2,
  Download
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { EANRequestsTable, exportEanCsv } from '@/components/EANRequestsTable';
import { EANRequestDetails } from '@/components/EANRequestDetails';
import { EANSubmittedDialog } from '@/components/EANSubmittedDialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TAX_RATES = ['5%', '12%', '18%', '28%'];

const STATUS_CONFIG = {
  pending: { label: 'Pending', icon: Clock, color: 'bg-yellow-100 text-yellow-700 border-yellow-200' },
  progress: { label: 'In Progress', icon: Loader2, color: 'bg-blue-100 text-blue-700 border-blue-200' },
  generated: { label: 'Generated', icon: CheckCircle2, color: 'bg-green-100 text-green-700 border-green-200' }
};

export default function EANRequestPage() {
  const { isAdmin, user: currentUser } = useAuth();
  // Only sweekar@wellversed.in can edit status / EAN / SKU / barcode image.
  // Everything else about the form (product info, images) is editable by the
  // requester too — see the backend PUT /ean-requests/{id} for enforcement.
  const [isEanAdmin, setIsEanAdmin] = useState(false);
  const [submittedResult, setSubmittedResult] = useState(null);
  useEffect(() => {
    fetch(`${API_URL}/api/ean-owners`, { credentials: 'include' })
      .then((r) => r.json()).then((d) => setIsEanAdmin(!!d.is_ean_admin)).catch(() => {});
  }, []);
  // Nice human name from email "first.last@wellversed.in" → "First Last"
  const nameFromEmail = (email) => {
    if (!email) return '—';
    const local = String(email).split('@')[0] || email;
    return local
      .split(/[._-]+/)
      .filter(Boolean)
      .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
      .join(' ') || email;
  };
  // Editable: any user can edit their own request while status is 'pending'.
  // Once it moves to 'progress' or 'generated', only the EAN admin can edit.
  // The backend enforces the same rule via PUT /ean-requests/{id}.
  // Only the submitter can edit the form, and only while it is Pending.
  const canEditRequest = (req) => !!req && req.status === 'pending'
    && (req.submitted_by || '').toLowerCase() === (currentUser?.email || '').toLowerCase();
  const [brands, setBrands] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFormModal, setShowFormModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState('all');
  // Edit mode — when set to a request id, the form modal saves via PUT
  // instead of POST and pre-fills with the existing data.
  const [editingId, setEditingId] = useState(null);
  // View-modal image slider index across [front, back, barcode]
  const [viewSlideIdx, setViewSlideIdx] = useState(0);
  
  // Form state
  const [formData, setFormData] = useState({
    brand_id: '',
    brand_name: '',
    product_name: '',
    flavour: '',
    product_description: '',
    country_of_origin: 'India',
    mrp: '',
    gross_weight_grams: '',
    net_weight_grams: '',
    hsn_code: '',
    tax_rate: '',
    storage_condition: '',
    front_image: null,
    back_image: null
  });
  
  const [frontImagePreview, setFrontImagePreview] = useState(null);
  const [backImagePreview, setBackImagePreview] = useState(null);

  // Download barcode image — supports both legacy base64 payloads and the
  // new GridFS URLs (produced by POST /ean-requests/{id}/barcode-image).
  const downloadBarcodeImage = async (image, productName, eanNumber) => {
    if (!image) {
      toast.error('No barcode image available');
      return;
    }
    const filename = `barcode_${eanNumber || (productName || 'ean').replace(/\s+/g, '_')}.png`;
    try {
      let blob;
      const isUrl = image.startsWith('http') || image.startsWith('/');
      if (isUrl) {
        // GridFS-backed URL — fetch it with credentials so the served bytes
        // (not the base64-decoded URL string) end up in the download.
        const src = image.startsWith('http') ? image : `${API_URL}${image}`;
        const res = await fetch(src, { credentials: 'include' });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        blob = await res.blob();
      } else {
        // Legacy base64 (raw or full data URL).
        const imageData = image.startsWith('data:') ? image : `data:image/png;base64,${image}`;
        const commaIdx = imageData.indexOf(',');
        const byteString = atob(imageData.slice(commaIdx + 1));
        const mimeType = (imageData.match(/^data:([^;]+);/) || [null, 'image/png'])[1];
        const buf = new ArrayBuffer(byteString.length);
        const view = new Uint8Array(buf);
        for (let i = 0; i < byteString.length; i++) view[i] = byteString.charCodeAt(i);
        blob = new Blob([buf], { type: mimeType });
      }
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);
      toast.success('Barcode image downloaded');
    } catch (error) {
      console.error('Download failed:', error);
      toast.error(`Failed to download barcode image: ${error.message}`);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [brandsRes, requestsRes] = await Promise.all([
        fetch(`${API_URL}/api/masters/brands`, { credentials: 'include' }),
        fetch(`${API_URL}/api/ean-requests`, { credentials: 'include' })
      ]);
      
      if (brandsRes.ok) setBrands(await brandsRes.json());
      if (requestsRes.ok) setRequests(await requestsRes.json());
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      brand_id: '',
      brand_name: '',
      product_name: '',
      flavour: '',
      product_description: '',
      country_of_origin: 'India',
      mrp: '',
      gross_weight_grams: '',
      net_weight_grams: '',
      hsn_code: '',
      tax_rate: '',
      storage_condition: '',
      front_image: null,
      back_image: null
    });
    setFrontImagePreview(null);
    setBackImagePreview(null);
  };

  const handleBrandChange = (brandId) => {
    const brand = brands.find(b => b.id === brandId);
    setFormData(prev => ({
      ...prev,
      brand_id: brandId,
      brand_name: brand?.name || ''
    }));
  };

  const handleImageUpload = (e, type) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }
    
    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be less than 5MB');
      return;
    }
    
    const reader = new FileReader();
    reader.onload = (event) => {
      const base64 = event.target.result;
      if (type === 'front') {
        setFormData(prev => ({ ...prev, front_image: base64 }));
        setFrontImagePreview(base64);
      } else {
        setFormData(prev => ({ ...prev, back_image: base64 }));
        setBackImagePreview(base64);
      }
    };
    reader.readAsDataURL(file);
  };

  const validateForm = () => {
    if (!formData.brand_id) {
      toast.error('Please select a brand');
      return false;
    }
    if (!formData.product_name.trim()) {
      toast.error('Product name is required');
      return false;
    }
    if (!formData.flavour.trim()) {
      toast.error('Flavour is required');
      return false;
    }
    if (!formData.product_description.trim()) {
      toast.error('Product description is required');
      return false;
    }
    if (formData.product_description.length > 200) {
      toast.error('Product description must be 200 characters or less');
      return false;
    }
    if (!formData.country_of_origin.trim()) {
      toast.error('Country of origin is required');
      return false;
    }
    if (!formData.mrp.trim()) {
      toast.error('MRP is required');
      return false;
    }
    if (!formData.gross_weight_grams.trim()) {
      toast.error('Gross weight is required');
      return false;
    }
    if (!formData.net_weight_grams.trim()) {
      toast.error('Net weight is required');
      return false;
    }
    if (!formData.hsn_code.trim() || !/^\d{6}$/.test(formData.hsn_code)) {
      toast.error('HSN code must be exactly 6 digits');
      return false;
    }
    if (!formData.tax_rate) {
      toast.error('Please select a tax rate');
      return false;
    }
    if (!formData.storage_condition.trim()) {
      toast.error('Storage condition is required');
      return false;
    }
    if (formData.storage_condition.includes(',')) {
      toast.error('Storage condition cannot contain commas');
      return false;
    }
    if (!formData.front_image) {
      toast.error('Front image is required');
      return false;
    }
    if (!formData.back_image) {
      toast.error('Back image is required');
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      const isEdit = !!editingId;
      const url = isEdit
        ? `${API_URL}/api/ean-requests/${editingId}`
        : `${API_URL}/api/ean-requests`;
      const response = await fetch(url, {
        method: isEdit ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(formData)
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.detail || 'Failed to submit request');
      }

      toast.success(isEdit ? 'EAN request updated' : 'EAN request submitted successfully!');
      if (!isEdit && result.request) setSubmittedResult(result);
      setShowFormModal(false);
      setEditingId(null);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.message || 'Failed to submit request');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Load an existing request into the form for editing. Called from both
  // the row actions and the "Edit" button inside the View modal.
  const openEdit = (req) => {
    setEditingId(req.id);
    setFormData({
      brand_id: req.brand_id || '',
      brand_name: req.brand_name || '',
      product_name: req.product_name || '',
      flavour: req.flavour || '',
      product_description: req.product_description || '',
      country_of_origin: req.country_of_origin || 'India',
      mrp: String(req.mrp || ''),
      gross_weight_grams: String(req.gross_weight_grams || ''),
      net_weight_grams: String(req.net_weight_grams || ''),
      hsn_code: req.hsn_code || '',
      tax_rate: req.tax_rate || '',
      storage_condition: req.storage_condition || '',
      front_image: req.front_image || null,
      back_image: req.back_image || null,
    });
    setFrontImagePreview(req.front_image || null);
    setBackImagePreview(req.back_image || null);
    setShowViewModal(false);
    setShowFormModal(true);
  };

  // Barcode-image upload (EAN admin only). Uses the new POST
  // /api/ean-requests/{id}/barcode-image endpoint which streams to GridFS
  // rather than base64-inlining a big image into the request doc.
  const uploadBarcodeImage = async (requestId, file) => {
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await fetch(`${API_URL}/api/ean-requests/${requestId}/barcode-image`, {
        method: 'POST', credentials: 'include', body: fd,
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || `HTTP ${r.status}`);
      toast.success('Barcode image uploaded');
      // Refresh the selected request so the slider shows the new image.
      const fresh = await fetch(`${API_URL}/api/ean-requests/${requestId}`, { credentials: 'include' });
      if (fresh.ok) {
        const updated = await fresh.json();
        setSelectedRequest(updated);
      }
      fetchData();
    } catch (e) {
      toast.error(`Upload failed: ${e.message}`);
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Are you sure you want to delete this request?')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/ean-requests/${id}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (!response.ok) throw new Error('Failed to delete');
      
      toast.success('Request deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete request');
    }
  };

  const handleStatusUpdate = async (id, newStatus, eanNumber = null, assignedSku = null, barcodeImage = null) => {
    try {
      const body = { status: newStatus };
      if (eanNumber) body.ean_number = eanNumber;
      if (assignedSku) body.assigned_sku = assignedSku;
      if (barcodeImage && !barcodeImage.includes('data:,')) body.barcode_image = barcodeImage;
      
      const response = await fetch(`${API_URL}/api/ean-requests/${id}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body)
      });
      
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Failed to update status');
      }
      
      toast.success(`Status updated to ${newStatus}`);
      fetchData();
      setShowViewModal(false);
    } catch (error) {
      toast.error(error.message);
    }
  };

  const filteredRequests = requests.filter(req => {
    if (activeTab === 'all') return true;
    return req.status === activeTab;
  });

  const getStatusBadge = (status) => {
    const config = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
    const Icon = config.icon;
    return (
      <Badge variant="outline" className={`${config.color} flex items-center gap-1`}>
        <Icon className="h-3 w-3" />
        {config.label}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-[#2EB4AC]" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">EAN Request</h1>
          <p className="text-slate-500 mt-1">Request EAN numbers for new product launches</p>
        </div>
        <Button 
          onClick={() => setShowFormModal(true)}
          className="bg-[#2EB4AC] hover:bg-[#269d96]"
          data-testid="new-ean-request-btn"
        >
          <Plus className="h-4 w-4 mr-2" />
          New Request
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {[
          { label: 'Total Requests', count: requests.length, color: 'bg-slate-100 text-slate-700' },
          { label: 'Pending', count: requests.filter(r => r.status === 'pending').length, color: 'bg-yellow-100 text-yellow-700' },
          { label: 'In Progress', count: requests.filter(r => r.status === 'progress').length, color: 'bg-blue-100 text-blue-700' },
          { label: 'Generated', count: requests.filter(r => r.status === 'generated').length, color: 'bg-green-100 text-green-700' }
        ].map((stat, idx) => (
          <Card key={idx} className="border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <p className="text-sm text-slate-500">{stat.label}</p>
                <Badge className={stat.color}>{stat.count}</Badge>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Requests Table */}
      <Card className="border-slate-200">
        <CardHeader className="pb-3 flex-row items-center justify-between space-y-0">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="bg-slate-100">
              <TabsTrigger value="all">All</TabsTrigger>
              <TabsTrigger value="pending">Pending</TabsTrigger>
              <TabsTrigger value="progress">In Progress</TabsTrigger>
              <TabsTrigger value="generated">Generated</TabsTrigger>
            </TabsList>
          </Tabs>
          <Button
            variant="outline"
            size="sm"
            onClick={() => exportEanCsv(filteredRequests, activeTab)}
            disabled={filteredRequests.length === 0}
            data-testid="ean-export-btn"
          >
            <Download className="h-4 w-4 mr-1.5" /> Export
          </Button>
        </CardHeader>
        <CardContent>
          {filteredRequests.length === 0 ? (
            <div className="text-center py-12">
              <Barcode className="h-12 w-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">No EAN requests found</p>
              <Button 
                variant="outline" 
                className="mt-4"
                onClick={() => setShowFormModal(true)}
              >
                <Plus className="h-4 w-4 mr-2" />
                Create First Request
              </Button>
            </div>
          ) : (
            <EANRequestsTable
              requests={filteredRequests}
              getStatusBadge={getStatusBadge}
              nameFromEmail={nameFromEmail}
              canDelete={(r) => isEanAdmin || canEditRequest(r)}
              canEdit={canEditRequest}
              onEdit={(req) => openEdit(req)}
              onDelete={handleDelete}
              onDownloadBarcode={(req) => downloadBarcodeImage(req.barcode_image, req.product_name, req.ean_number)}
              onView={(req) => { setSelectedRequest(req); setViewSlideIdx(0); setShowViewModal(true); }}
            />
          )}
        </CardContent>
      </Card>

      {/* New / Edit Request Form Modal */}
      <Dialog open={showFormModal} onOpenChange={(open) => {
        setShowFormModal(open);
        if (!open) {
          resetForm();
          setEditingId(null);
        }
      }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Barcode className="h-5 w-5 text-[#2EB4AC]" />
              {editingId ? 'Edit EAN Request' : 'New EAN Request'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Brand Selection */}
            <div>
              <Label className="text-slate-700 font-medium">Brand *</Label>
              <Select value={formData.brand_id} onValueChange={handleBrandChange}>
                <SelectTrigger className="mt-2" data-testid="ean-brand-select">
                  <SelectValue placeholder="Select brand" />
                </SelectTrigger>
                <SelectContent>
                  {brands.map((brand) => (
                    <SelectItem key={brand.id} value={brand.id}>
                      {brand.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Product Name */}
            <div>
              <Label className="text-slate-700 font-medium">Product Name *</Label>
              <Input
                value={formData.product_name}
                onChange={(e) => setFormData(prev => ({ ...prev, product_name: e.target.value }))}
                placeholder="e.g., Whey Protein Isolate 1kg"
                className="mt-2"
                data-testid="ean-product-name"
              />
            </div>

            {/* Flavour */}
            <div>
              <Label className="text-slate-700 font-medium">Flavour *</Label>
              <Input
                value={formData.flavour}
                onChange={(e) => setFormData(prev => ({ ...prev, flavour: e.target.value }))}
                placeholder="e.g., Chocolate, Vanilla"
                className="mt-2"
                data-testid="ean-flavour"
              />
            </div>

            {/* Product Description */}
            <div>
              <Label className="text-slate-700 font-medium">
                Product Description * 
                <span className="text-slate-400 font-normal ml-2">
                  ({formData.product_description.length}/200)
                </span>
              </Label>
              <Textarea
                value={formData.product_description}
                onChange={(e) => setFormData(prev => ({ ...prev, product_description: e.target.value.slice(0, 200) }))}
                placeholder="Brief product description (max 200 characters)"
                className="mt-2"
                rows={2}
                data-testid="ean-description"
              />
            </div>

            {/* Two Column Layout */}
            <div className="grid grid-cols-2 gap-4">
              {/* Country of Origin */}
              <div>
                <Label className="text-slate-700 font-medium">Country of Origin *</Label>
                <Input
                  value={formData.country_of_origin}
                  onChange={(e) => setFormData(prev => ({ ...prev, country_of_origin: e.target.value }))}
                  placeholder="India"
                  className="mt-2"
                  data-testid="ean-country"
                />
              </div>

              {/* MRP */}
              <div>
                <Label className="text-slate-700 font-medium">MRP (₹) *</Label>
                <Input
                  value={formData.mrp}
                  onChange={(e) => setFormData(prev => ({ ...prev, mrp: e.target.value }))}
                  placeholder="e.g., 2499"
                  className="mt-2"
                  data-testid="ean-mrp"
                />
              </div>

              {/* Gross Weight */}
              <div>
                <Label className="text-slate-700 font-medium">Gross Weight (Grams) *</Label>
                <Input
                  value={formData.gross_weight_grams}
                  onChange={(e) => setFormData(prev => ({ ...prev, gross_weight_grams: e.target.value }))}
                  placeholder="e.g., 1100"
                  className="mt-2"
                  data-testid="ean-gross-weight"
                />
              </div>

              {/* Net Weight */}
              <div>
                <Label className="text-slate-700 font-medium">Net Weight (Grams) *</Label>
                <Input
                  value={formData.net_weight_grams}
                  onChange={(e) => setFormData(prev => ({ ...prev, net_weight_grams: e.target.value }))}
                  placeholder="e.g., 1000"
                  className="mt-2"
                  data-testid="ean-net-weight"
                />
              </div>

              {/* HSN Code */}
              <div>
                <Label className="text-slate-700 font-medium">HSN Code (6 Digits) *</Label>
                <Input
                  value={formData.hsn_code}
                  onChange={(e) => setFormData(prev => ({ ...prev, hsn_code: e.target.value.replace(/\D/g, '').slice(0, 6) }))}
                  placeholder="e.g., 210690"
                  className="mt-2"
                  maxLength={6}
                  data-testid="ean-hsn"
                />
              </div>

              {/* Tax Rate */}
              <div>
                <Label className="text-slate-700 font-medium">Tax Rate *</Label>
                <Select value={formData.tax_rate} onValueChange={(v) => setFormData(prev => ({ ...prev, tax_rate: v }))}>
                  <SelectTrigger className="mt-2" data-testid="ean-tax-rate">
                    <SelectValue placeholder="Select tax rate" />
                  </SelectTrigger>
                  <SelectContent>
                    {TAX_RATES.map((rate) => (
                      <SelectItem key={rate} value={rate}>{rate}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Storage Condition */}
            <div>
              <Label className="text-slate-700 font-medium">Storage Condition *</Label>
              <Input
                value={formData.storage_condition}
                onChange={(e) => setFormData(prev => ({ ...prev, storage_condition: e.target.value.replace(/,/g, '') }))}
                placeholder="e.g., Store in a cool and dry place"
                className="mt-2"
                data-testid="ean-storage"
              />
              <p className="text-xs text-slate-500 mt-1">Note: Commas are not allowed</p>
            </div>

            {/* Image Uploads */}
            <div className="border-t pt-4 mt-4">
              <Label className="text-slate-700 font-medium flex items-center gap-2 mb-3">
                <ImageIcon className="h-4 w-4" />
                Product Images (1024 x 1024) *
              </Label>
              
              <div className="grid grid-cols-2 gap-4">
                {/* Front Image */}
                <div>
                  <p className="text-sm text-slate-600 mb-2">Front Image *</p>
                  <div className={`border-2 border-dashed rounded-lg p-4 text-center transition-colors ${!formData.front_image ? 'border-red-200 bg-red-50/30' : 'border-slate-200 hover:border-[#2EB4AC]'}`}>
                    {frontImagePreview ? (
                      <div className="relative">
                        <img 
                          src={frontImagePreview} 
                          alt="Front preview" 
                          className="w-full h-32 object-contain rounded"
                        />
                        <Button
                          variant="ghost"
                          size="sm"
                          className="absolute top-0 right-0 h-6 w-6 p-0"
                          onClick={() => {
                            setFrontImagePreview(null);
                            setFormData(prev => ({ ...prev, front_image: null }));
                          }}
                        >
                          <XCircle className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    ) : (
                      <label className="cursor-pointer block">
                        <Upload className="h-8 w-8 text-slate-400 mx-auto mb-2" />
                        <p className="text-sm text-slate-500">Click to upload</p>
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={(e) => handleImageUpload(e, 'front')}
                          data-testid="ean-front-image"
                        />
                      </label>
                    )}
                  </div>
                </div>

                {/* Back Image */}
                <div>
                  <p className="text-sm text-slate-600 mb-2">Back Image *</p>
                  <div className={`border-2 border-dashed rounded-lg p-4 text-center transition-colors ${!formData.back_image ? 'border-red-200 bg-red-50/30' : 'border-slate-200 hover:border-[#2EB4AC]'}`}>
                    {backImagePreview ? (
                      <div className="relative">
                        <img 
                          src={backImagePreview} 
                          alt="Back preview" 
                          className="w-full h-32 object-contain rounded"
                        />
                        <Button
                          variant="ghost"
                          size="sm"
                          className="absolute top-0 right-0 h-6 w-6 p-0"
                          onClick={() => {
                            setBackImagePreview(null);
                            setFormData(prev => ({ ...prev, back_image: null }));
                          }}
                        >
                          <XCircle className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    ) : (
                      <label className="cursor-pointer block">
                        <Upload className="h-8 w-8 text-slate-400 mx-auto mb-2" />
                        <p className="text-sm text-slate-500">Click to upload</p>
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={(e) => handleImageUpload(e, 'back')}
                          data-testid="ean-back-image"
                        />
                      </label>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowFormModal(false); setEditingId(null); }}>
              Cancel
            </Button>
            <Button 
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="bg-[#2EB4AC] hover:bg-[#269d96]"
              data-testid="submit-ean-request"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {editingId ? 'Updating...' : 'Submitting...'}
                </>
              ) : (
                <>
                  <Barcode className="h-4 w-4 mr-2" />
                  {editingId ? 'Update Request' : 'Submit Request'}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <EANSubmittedDialog result={submittedResult} onClose={() => setSubmittedResult(null)} />

      {/* View Request Modal */}
      <Dialog open={showViewModal} onOpenChange={setShowViewModal}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto p-0">
          <DialogHeader className="px-6 pt-6 pb-4 border-b border-slate-100">
            <DialogTitle className="flex items-center gap-2">
              <Package className="h-5 w-5 text-[#2EB4AC]" />
              EAN Request Details
            </DialogTitle>
          </DialogHeader>

          {selectedRequest && (
            <div className="px-6 py-6 space-y-0">
              {/* Header row: product identity + status + edit */}
              <div className="flex items-start justify-between gap-3 pb-6">
                <div className="min-w-0">
                  <h3 className="font-semibold text-xl text-slate-900 truncate" data-testid="ean-view-product-name">{selectedRequest.product_name}</h3>
                  <div className="flex items-center gap-2 mt-1 text-sm text-slate-500">
                    <span>{selectedRequest.brand_name}</span>
                    {selectedRequest.flavour && (
                      <>
                        <span className="text-slate-300">•</span>
                        <span>{selectedRequest.flavour}</span>
                      </>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {getStatusBadge(selectedRequest.status)}
                  {canEditRequest(selectedRequest) ? (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openEdit(selectedRequest)}
                      className="h-8 text-[#2EB4AC] border-[#2EB4AC] hover:bg-[#2EB4AC]/10"
                      data-testid="ean-view-edit-btn"
                    >
                      Edit
                    </Button>
                  ) : (
                    selectedRequest.status !== 'pending' && (
                      <span className="text-xs text-slate-400 italic" title="Locked once status moves out of Pending">Locked</span>
                    )
                  )}
                </div>
              </div>

              <div className="border-t border-slate-200 pt-6 pb-2">
                <EANRequestDetails
                  req={selectedRequest}
                  onDownloadBarcode={(r) => downloadBarcodeImage(r.barcode_image, r.product_name, r.ean_number)}
                />
              </div>

              {/* Section: Timeline */}
              <section className="py-6 border-t border-slate-200">
                <h4 className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-4">Timeline</h4>
                <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
                  <div>
                    <p className="text-slate-500 text-xs">Submitted by</p>
                    <p className="font-medium text-slate-900 mt-0.5">{nameFromEmail(selectedRequest.submitted_by)}</p>
                    <p className="text-xs text-slate-400 truncate" title={selectedRequest.submitted_by}>{selectedRequest.submitted_by}</p>
                  </div>
                  <div>
                    <p className="text-slate-500 text-xs">Submitted at</p>
                    <p className="font-medium text-slate-900 mt-0.5">{new Date(selectedRequest.submitted_at).toLocaleString()}</p>
                  </div>
                  {selectedRequest.last_edited_by && (
                    <div className="col-span-2 pt-3 mt-2 border-t border-slate-100">
                      <p className="text-slate-500 text-xs">Last edited by</p>
                      <p className="font-medium text-slate-900 mt-0.5">{nameFromEmail(selectedRequest.last_edited_by)}</p>
                      {selectedRequest.updated_at && (
                        <p className="text-xs text-slate-400">{new Date(selectedRequest.updated_at).toLocaleString()}</p>
                      )}
                    </div>
                  )}
                </div>
              </section>

              {/* Section: Admin Actions — gated to sweekar@wellversed.in */}
              {isEanAdmin && (selectedRequest.status === 'pending' || selectedRequest.status === 'progress') && (
                <section className="py-6 border-t border-slate-200">
                  <div className="rounded-lg border border-[#2EB4AC]/30 bg-[#2EB4AC]/5 p-4 space-y-4">
                    <div className="flex items-center gap-2">
                      <div className="h-6 w-1 bg-[#2EB4AC] rounded-full" />
                      <h4 className="text-xs uppercase tracking-wider text-[#2EB4AC] font-semibold">Admin Actions</h4>
                    </div>

                    {selectedRequest.status === 'pending' && (
                      <Button
                        className="w-full bg-blue-600 hover:bg-blue-700"
                        onClick={() => handleStatusUpdate(selectedRequest.id, 'progress')}
                        data-testid="ean-mark-progress-btn"
                      >
                        <Loader2 className="h-4 w-4 mr-2" />
                        Mark as In Progress
                      </Button>
                    )}

                    {selectedRequest.status === 'progress' && (
                      <div className="space-y-3">
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <Label className="text-slate-700 font-medium">EAN Number *</Label>
                            <Input placeholder="13-digit EAN" id="assign-ean" className="mt-1 font-mono" data-testid="ean-assign-input" />
                          </div>
                          <div>
                            <Label className="text-slate-700 font-medium">Assigned SKU</Label>
                            <Input placeholder="e.g., WLC-XYZ-001" id="assign-sku" className="mt-1 font-mono" data-testid="ean-assign-sku" />
                          </div>
                        </div>
                        <div>
                          <Label className="text-slate-700 font-medium">Barcode Image (optional)</Label>
                          <input
                            type="file"
                            accept="image/png,image/jpeg,image/webp,image/gif"
                            className="mt-1 block w-full text-sm text-slate-600 file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:bg-[#2EB4AC]/10 file:text-[#2EB4AC] file:font-medium hover:file:bg-[#2EB4AC]/20"
                            data-testid="ean-barcode-upload"
                            onChange={(e) => {
                              const f = e.target.files?.[0];
                              if (f) uploadBarcodeImage(selectedRequest.id, f);
                              e.target.value = '';
                            }}
                          />
                          <p className="text-xs text-slate-500 mt-1">Streams to GridFS. Max 8 MB. PNG/JPG/WebP/GIF.</p>
                        </div>
                        <Button
                          className="w-full bg-emerald-600 hover:bg-emerald-700"
                          data-testid="ean-mark-generated-btn"
                          onClick={() => {
                            const ean = document.getElementById('assign-ean').value;
                            const sku = document.getElementById('assign-sku').value;
                            if (!ean || !/^\d{13}$/.test(ean)) {
                              toast.error('EAN must be exactly 13 digits');
                              return;
                            }
                            handleStatusUpdate(selectedRequest.id, 'generated', ean, sku);
                          }}
                        >
                          <CheckCircle2 className="h-4 w-4 mr-2" />
                          Mark as Generated
                        </Button>
                      </div>
                    )}
                  </div>
                </section>
              )}

              {/* Admin: re-upload / replace barcode after generation */}
              {isEanAdmin && selectedRequest.status === 'generated' && (
                <section className="py-6 border-t border-slate-200">
                  <h4 className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-3">Replace Barcode Image</h4>
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/webp,image/gif"
                    className="block w-full text-sm text-slate-600 file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:bg-[#2EB4AC]/10 file:text-[#2EB4AC] file:font-medium hover:file:bg-[#2EB4AC]/20"
                    data-testid="ean-barcode-upload"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) uploadBarcodeImage(selectedRequest.id, f);
                      e.target.value = '';
                    }}
                  />
                </section>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
